import { usePersistedState, useScrollMemory } from '../../hooks/usePersistedState';
import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Button,
  Space,
  Typography,
  Card,
  Row,
  Col,
  Input,
  DatePicker,
  Select,
  Tooltip,
  Tag,
  Tabs,
} from 'antd';
import ConfigurableTable from '../../components/ConfigurableTable';
import {
  DensityToggle,
  useDensity,
  DocCell,
  PersonCell,
  FilterChips,
} from '../../components/table-cells';
import { InboxOutlined, EyeOutlined } from '@ant-design/icons';
import dayjs, { Dayjs } from 'dayjs';
import { listReceipts, getLookups, ReceiptRow, ReceiptList } from '../../api';
import { useAuth } from '../../context/AuthContext';
import ExpectedDeliveries from '../../components/receiving/ExpectedDeliveries';
import PrintSlips from '../../components/receiving/PrintSlips';
import { money } from '../../components/receiving/shared';

const { Title, Text } = Typography;

const dateRangeCodec = {
  serialize: (v: [Dayjs | null, Dayjs | null] | null) => (v ? [v[0] ? v[0].format('YYYY-MM-DD') : null, v[1] ? v[1].format('YYYY-MM-DD') : null] : null),
  deserialize: (raw: any): [Dayjs | null, Dayjs | null] | null => (raw ? [raw[0] ? dayjs(raw[0]) : null, raw[1] ? dayjs(raw[1]) : null] : null),
};

// GRN mockup (2026-10-22): the receipts list shows the path, the received date, the value and where it goes; the
// filters are the ones that need action (rejections still to resolve, no proof). Counts come from the server and
// start at go-live for the new filters; older receipts show "before redesign".
const PATH_TAG: Record<string, { label: string; color: string }> = { direct: { label: 'Direct', color: 'purple' }, order: { label: 'Vendor order', color: 'blue' } };

const GRNList: React.FC = () => {
  const navigate = useNavigate();
  // GRN phase 1 (2026-10-21): Goods Receiving opens on what is due; the receipts list moves to the second tab.
  const [tab, setTab] = useState<'expected' | 'receipts'>('expected');
  const { canAccess } = useAuth();
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState<ReceiptRow[]>([]);
  const [total, setTotal] = useState(0);
  const [counts, setCounts] = useState<ReceiptList['counts'] | null>(null);
  const [page, setPage] = usePersistedState('grns', 'page', 1);

  // Filter state
  const [searchText, setSearchText] = usePersistedState<string>('grns', 'search', '');
  const [filterVendors, setFilterVendors] = usePersistedState<number[]>('grns', 'vendors', []);
  const [dateRange, setDateRange] = usePersistedState<[Dayjs | null, Dayjs | null] | null>('grns', 'dateRange', null, dateRangeCodec);
  const [pathChip, setPathChip] = usePersistedState<string | null>('grns', 'path', null);
  const [attention, setAttention] = usePersistedState<string | null>('grns', 'attention', null);
  useScrollMemory('grns', !loading);
  const [vendorOptions, setVendorOptions] = useState<Array<{ label: string; value: number }>>([]);

  const [density, setDensity] = useDensity('grn-list');
  const compact = density === 'compact';

  // Debounce ref for search
  const debounceTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Load vendor lookups once
  useEffect(() => {
    getLookups()
      .then((l) => {
        setVendorOptions(
          (l.vendors || []).map((v: any) => ({ label: v.name, value: v.id }))
        );
      })
      .catch(() => {});
  }, []);

  const fetchGRNs = useCallback(
    async (overridePage?: number) => {
      setLoading(true);
      try {
        const result = await listReceipts({
          page: overridePage ?? page,
          limit: 25,
          search: searchText.trim() || undefined,
          vendorIds: filterVendors.length > 0 ? filterVendors.join(',') : undefined,
          startDate: dateRange && dateRange[0] ? dateRange[0].format('YYYY-MM-DD') : undefined,
          endDate: dateRange && dateRange[1] ? dateRange[1].format('YYYY-MM-DD') : undefined,
          path: (pathChip as 'direct' | 'order' | null) || undefined,
          attention: (attention as 'rejections' | 'noproof' | null) || undefined,
        });
        setData(result.data);
        setTotal(result.total);
        setCounts(result.counts);
      } catch (err) {
        console.error('Failed to load receipts', err);
      } finally {
        setLoading(false);
      }
    },
    [page, searchText, filterVendors, dateRange, pathChip, attention]
  );

  useEffect(() => {
    fetchGRNs();
  }, [fetchGRNs]);

  const handleSearchChange = (value: string) => {
    setSearchText(value);
    if (debounceTimer.current) clearTimeout(debounceTimer.current);
    debounceTimer.current = setTimeout(() => {
      setPage(1);
    }, 300);
  };

  const handleVendorChange = (values: number[]) => {
    setFilterVendors(values);
    setPage(1);
  };

  const handleDateChange = (dates: [Dayjs | null, Dayjs | null] | null) => {
    setDateRange(dates);
    setPage(1);
  };

  const columns: any[] = [
    {
      title: 'Receipt',
      key: 'grnId',
      width: 140,
      render: (_: any, r: ReceiptRow) => <DocCell id={r.grnId} linkTo={`/grns/${r.grnId}`} compact={compact} />,
    },
    {
      title: 'PO · Vendor',
      key: 'poVendor',
      width: 210,
      render: (_: any, r: ReceiptRow) => r.po ? (
        <DocCell id={r.po.poId} linkTo={`/pos/${r.po.id}`} meta={r.vendor ? `${r.vendor.name}${r.vendor.appCode ? ` · ${r.vendor.appCode}` : ''}` : null} compact={compact} />
      ) : <Text type="secondary">no PO</Text>,
    },
    {
      title: 'Path',
      key: 'path',
      width: 130,
      render: (_: any, r: ReceiptRow) => r.path
        ? <Tag color={PATH_TAG[r.path].color} style={{ margin: 0 }}>{PATH_TAG[r.path].label}</Tag>
        : <Tag style={{ margin: 0 }}>before redesign</Tag>,
    },
    {
      title: 'Received',
      key: 'received',
      width: 140,
      render: (_: any, r: ReceiptRow) => (
        <PersonCell name={r.receivedOn ? dayjs(r.receivedOn).format('D MMM') : null} sub={r.receivedBy?.displayName} compact={compact} missingLabel="no date" />
      ),
    },
    {
      title: 'Value',
      key: 'value',
      width: 120,
      align: 'right' as const,
      render: (_: any, r: ReceiptRow) => (r.value > 0 ? money(r.value) : <Text type="secondary">—</Text>),
    },
    {
      title: 'Goes to',
      key: 'goesTo',
      render: (_: any, r: ReceiptRow) => (
        <span>
          {r.goesTo.length ? r.goesTo.slice(0, 3).map((c) => <Tooltip key={c.code} title={c.name}><code style={{ marginRight: 6 }}>{c.code}</code></Tooltip>) : <Text type="secondary">Not recorded</Text>}
          {r.goesTo.length > 3 && <Text type="secondary">+{r.goesTo.length - 3}</Text>}
          {(r.toResolve || r.noProofReason) && (
            <div style={{ fontSize: 12, color: r.toResolve ? '#cf1322' : '#ad6800' }}>
              {r.toResolve ? `${r.rejected} rejected · to resolve` : 'no receipt (reason given)'}
            </div>
          )}
        </span>
      ),
    },
    {
      title: '',
      key: 'actions',
      width: 60,
      render: (_: any, r: ReceiptRow) => (
        <Tooltip title="View receipt">
          <Button type="text" size="small" icon={<EyeOutlined />} onClick={() => navigate(`/grns/${r.grnId}`)} />
        </Tooltip>
      ),
    },
  ];

  return (
    <div>
      <Row justify="space-between" align="middle" style={{ marginBottom: 16 }}>
        <Col>
          <Space align="start">
            <InboxOutlined style={{ fontSize: 24, color: '#1890ff' }} />
            <div>
              <Title level={4} style={{ margin: 0 }}>Goods Receiving</Title>
              <Text type="secondary" style={{ fontSize: 12 }}>{(counts?.all ?? total).toLocaleString('en-US')} receipts</Text>
            </div>
          </Space>
        </Col>
        <Col>
          {tab === 'receipts' ? <DensityToggle value={density} onChange={setDensity} /> : <PrintSlips />}
        </Col>
      </Row>

      <Tabs
        activeKey={tab}
        onChange={(k) => setTab(k as 'expected' | 'receipts')}
        items={[{ key: 'expected', label: 'Expected deliveries' }, { key: 'receipts', label: 'Receipts' }]}
        style={{ marginBottom: 8 }}
      />
      {tab === 'expected' ? (
        <ExpectedDeliveries
          canReceive={canAccess('purchasing.grns', 'create')}
          onShowRejections={() => { setPathChip(null); setAttention('rejections'); setPage(1); setTab('receipts'); }}
        />
      ) : (
      <>

      {/* Filter bar */}
      <Card size="small" style={{ marginBottom: 16 }}>
        <Row gutter={[12, 12]} align="middle">
          <Col xs={24} sm={24} md={8}>
            <Input.Search
              placeholder="Search receipt, PO or vendor"
              value={searchText}
              onChange={(e) => handleSearchChange(e.target.value)}
              onSearch={() => {
                setPage(1);
                fetchGRNs(1);
              }}
              allowClear
            />
          </Col>
          <Col xs={24} sm={12} md={8}>
            <Select
              mode="multiple"
              placeholder="Vendor"
              allowClear
              style={{ width: '100%' }}
              value={filterVendors}
              onChange={handleVendorChange}
              options={vendorOptions}
              maxTagCount="responsive"
              optionFilterProp="label"
            />
          </Col>
          <Col xs={24} sm={12} md={8}>
            <DatePicker.RangePicker
              style={{ width: '100%' }}
              placeholder={['Received From', 'Received To']}
              value={dateRange as any}
              onChange={(dates) => handleDateChange(dates as [Dayjs | null, Dayjs | null] | null)}
            />
          </Col>
        </Row>
      </Card>

      <FilterChips
        style={{ marginBottom: 12 }}
        allCount={counts?.all ?? null}
        value={pathChip}
        onChange={(k) => { setPathChip(k); setPage(1); }}
        chips={[
          { key: 'direct', label: 'Direct purchase', count: counts?.direct ?? null },
          { key: 'order', label: 'Vendor order', count: counts?.order ?? null },
        ]}
        attention={[
          { key: 'rejections', label: 'Rejections to resolve', count: counts?.rejections ?? null, tone: 'bad' },
          { key: 'noproof', label: 'No proof', count: counts?.noProof ?? null, tone: 'warn' },
        ]}
        attentionValue={attention ? [attention] : []}
        onAttentionChange={(keys) => { setAttention(keys.find((k) => k !== attention) ?? null); setPage(1); }}
      />

      <Card>
        <ConfigurableTable
          tableId="grn-list"
          card={{ header: ['grnId'], footer: ['actions'] }}
          columns={columns}
          dataSource={data}
          rowKey="id"
          loading={loading}
          size={compact ? 'small' : 'middle'}
          scroll={{ x: 1000 }}
          pagination={{
            current: page,
            total,
            pageSize: 25,
            showSizeChanger: false,
            onChange: (p) => setPage(p),
            showTotal: (t) => `${t} receipts`,
          }}
        />
      </Card>
      </>
      )}
    </div>
  );
};

export default GRNList;
